import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "src/components/ui/button";
import Page from "src/layouts/Page";
import ContentTable, {
	type ContentTableColumn,
	type ContentTableItem
} from "src/components/ContentTable/ContentTable";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import useAdminRegions from "../../hooks/useAdminRegions";
import useAdminAreas from "../../hooks/useAdminAreas";
import useAdminStores from "../../hooks/useAdminStores";
import useDeleteRegion from "../../hooks/useDeleteRegion";
import useDeleteArea from "../../hooks/useDeleteArea";
import useDeleteStore from "../../hooks/useDeleteStore";
import { mapRegionsToContentTableItems } from "../../utils/mapRegionsToContentTableItems";
import { mapAreasToContentTableItems } from "../../utils/mapAreasToContentTableItems";
import { mapStoresToContentTableItems } from "../../utils/mapStoresToContentTableItems";

type Tab = "regions" | "areas" | "stores";

const TABS: { key: Tab; label: string }[] = [
	{ key: "regions", label: "Regions" },
	{ key: "areas", label: "Areas" },
	{ key: "stores", label: "Stores" }
];

const regionColumns: ContentTableColumn[] = [
	{
		key: "manager",
		label: "Region Manager",
		render: (item) => String(item.data?.manager ?? "—")
	},
	{
		key: "lastsync",
		label: "Last Sync",
		render: (item) => {
			const val = item.data?.lastsync;
			if (!val) return "—";
			return new Date(String(val)).toLocaleDateString("en-GB", {
				day: "numeric",
				month: "short",
				year: "numeric"
			});
		}
	}
];

const areaColumns: ContentTableColumn[] = [
	{
		key: "region",
		label: "Region",
		render: (item) => String(item.data?.region ?? "—")
	},
	{
		key: "manager",
		label: "Area Manager",
		render: (item) => String(item.data?.manager ?? "—")
	}
];

const storeColumns: ContentTableColumn[] = [
	{
		key: "region",
		label: "Region",
		render: (item) => String(item.data?.region ?? "—")
	},
	{
		key: "area",
		label: "Area",
		render: (item) => String(item.data?.area ?? "—")
	}
];

const Locations = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const rawTab = searchParams.get("tab");
	const activeTab: Tab =
		rawTab === "areas" || rawTab === "stores" ? rawTab : "regions";

	const [itemToDelete, setItemToDelete] = useState<ContentTableItem | null>(
		null
	);

	// Filter state
	const [regionFilter, setRegionFilter] = useState<number | undefined>(
		undefined
	);
	const [areaFilter, setAreaFilter] = useState<number | undefined>(undefined);

	// Data hooks
	const {
		data: regions = [],
		isLoading: isLoadingRegions,
		isError: isErrorRegions
	} = useAdminRegions();

	const {
		data: areas = [],
		isLoading: isLoadingAreas,
		isError: isErrorAreas
	} = useAdminAreas(regionFilter);

	const {
		data: stores = [],
		isLoading: isLoadingStores,
		isError: isErrorStores
	} = useAdminStores(
		activeTab === "stores" ? { regionId: regionFilter, areaId: areaFilter } : {}
	);

	// Delete hooks
	const { mutate: deleteRegion, isLoading: isDeletingRegion } =
		useDeleteRegion();
	const { mutate: deleteArea, isLoading: isDeletingArea } = useDeleteArea();
	const { mutate: deleteStore, isLoading: isDeletingStore } = useDeleteStore();

	const isDeleting = isDeletingRegion || isDeletingArea || isDeletingStore;

	const handleTabChange = (tab: Tab) => {
		setSearchParams({ tab });
		setRegionFilter(undefined);
		setAreaFilter(undefined);
	};

	const handleDelete = () => {
		if (!itemToDelete) return;
		const id = Number(itemToDelete.id);

		if (activeTab === "regions") {
			deleteRegion(id, { onSuccess: () => setItemToDelete(null) });
		} else if (activeTab === "areas") {
			deleteArea(id, { onSuccess: () => setItemToDelete(null) });
		} else {
			deleteStore(id, { onSuccess: () => setItemToDelete(null) });
		}
	};

	const isLoading =
		(activeTab === "regions" && isLoadingRegions) ||
		(activeTab === "areas" && isLoadingAreas) ||
		(activeTab === "stores" && isLoadingStores);

	const isError =
		(activeTab === "regions" && isErrorRegions) ||
		(activeTab === "areas" && isErrorAreas) ||
		(activeTab === "stores" && isErrorStores);

	const regionItems = mapRegionsToContentTableItems(regions);
	const areaItems = mapAreasToContentTableItems(areas);
	const storeItems = mapStoresToContentTableItems(stores);

	return (
		<Page>
			{/* Tab buttons */}
			<div
				className="inline-flex bg-white border border-border p-1.5 rounded-xl shadow-xs mb-6"
				role="tablist"
			>
				{TABS.map(({ key, label }) => (
					<button
						key={key}
						type="button"
						role="tab"
						aria-selected={activeTab === key}
						className={[
							"flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200",
							activeTab === key
								? "bg-primary text-white shadow-md shadow-primary/50"
								: "text-zinc-600 hover:text-foreground hover:bg-white/80"
						].join(" ")}
						onClick={() => handleTabChange(key)}
					>
						{label}
					</button>
				))}
			</div>

			{isLoading && (
				<div
					className="flex items-center gap-2 py-12 justify-center"
					data-testid="locations-spinner"
				>
					<div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
					<span className="text-sm text-zinc-600">Loading...</span>
				</div>
			)}

			{isError && !isLoading && (
				<p className="text-sm text-destructive py-8 text-center">
					Something went wrong. Please try again.
				</p>
			)}

			{!isLoading && !isError && activeTab === "regions" && (
				<ContentTable
					columns={regionColumns}
					items={regionItems}
					onRowClick={(item) => navigate(`/users/locations/regions/${item.id}`)}
					onDelete={(item) => setItemToDelete(item)}
					createPath="/users/locations/regions/create"
					createLabel="Create Region"
				/>
			)}

			{!isLoading && !isError && activeTab === "areas" && (
				<ContentTable
					columns={areaColumns}
					items={areaItems}
					onRowClick={(item) => navigate(`/users/locations/areas/${item.id}`)}
					onDelete={(item) => setItemToDelete(item)}
					createPath="/users/locations/areas/create"
					createLabel="Create Area"
					filterSlot={
						regions.length > 0 ? (
							<div className="bg-white border border-border rounded-xl p-4 shadow-xs">
								<div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-center">
									<select
										className="w-full px-3 py-2.5 bg-white border border-border rounded-lg text-base font-medium focus:outline-none focus:ring-2 focus:ring-ring"
										value={regionFilter ?? ""}
										onChange={(e) => {
											const val = e.target.value;
											setRegionFilter(val ? Number(val) : undefined);
										}}
										aria-label="Filter by region"
									>
										<option value="">All Regions</option>
										{regions.map((r) => (
											<option key={r.id} value={r.id}>
												{r.name}
											</option>
										))}
									</select>
									<Button
										variant="outline"
										onClick={() => setRegionFilter(undefined)}
										disabled={!regionFilter}
									>
										Reset
									</Button>
								</div>
							</div>
						) : undefined
					}
				/>
			)}

			{!isLoading && !isError && activeTab === "stores" && (
				<ContentTable
					columns={storeColumns}
					items={storeItems}
					onRowClick={(item) => navigate(`/users/locations/stores/${item.id}`)}
					onDelete={(item) => setItemToDelete(item)}
					createPath="/users/locations/stores/create"
					createLabel="Create Store"
					filterSlot={
						regions.length > 0 ? (
							<div className="bg-white border border-border rounded-xl p-4 shadow-xs">
								<div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-center">
									<select
										className="w-full px-3 py-2.5 bg-white border border-border rounded-lg text-base font-medium focus:outline-none focus:ring-2 focus:ring-ring"
										value={regionFilter ?? ""}
										onChange={(e) => {
											const val = e.target.value;
											setRegionFilter(val ? Number(val) : undefined);
											setAreaFilter(undefined);
										}}
										aria-label="Filter by region"
									>
										<option value="">All Regions</option>
										{regions.map((r) => (
											<option key={r.id} value={r.id}>
												{r.name}
											</option>
										))}
									</select>

									<select
										className="w-full px-3 py-2.5 bg-white border border-border rounded-lg text-base font-medium focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
										value={areaFilter ?? ""}
										onChange={(e) => {
											const val = e.target.value;
											setAreaFilter(val ? Number(val) : undefined);
										}}
										aria-label="Filter by area"
										disabled={!regionFilter}
									>
										<option value="">All Areas</option>
										{areas
											.filter(
												(a) => !regionFilter || a.region?.id === regionFilter
											)
											.map((a) => (
												<option key={a.id} value={a.id}>
													{a.name}
												</option>
											))}
									</select>

									<Button
										variant="outline"
										onClick={() => {
											setRegionFilter(undefined);
											setAreaFilter(undefined);
										}}
										disabled={!regionFilter && !areaFilter}
									>
										Reset All
									</Button>
								</div>
							</div>
						) : undefined
					}
				/>
			)}

			<ConfirmationModal
				isOpen={itemToDelete !== null}
				title={`Delete ${activeTab.slice(0, -1)}?`}
				description="This action cannot be undone."
				onCancel={() => {
					if (!isDeleting) setItemToDelete(null);
				}}
				onSubmit={handleDelete}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default Locations;
